import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";

import { Picker, Dialog } from "antd-mobile";
import { toastSuccess, toastWarning } from "@/utils/toast";
import { getStaffDetail, updateTakeTime } from "@/api/staff";

// ── 常量 ──
const BRAND = "#FF6B35";

// 与 Vue 版本 weekData 保持一致：value 1-7，周日=7
const WEEK_DATA = [
  { key: 1, label: "星期一" },
  { key: 2, label: "星期二" },
  { key: 3, label: "星期三" },
  { key: 4, label: "星期四" },
  { key: 5, label: "星期五" },
  { key: 6, label: "星期六" },
  { key: 7, label: "星期日" },
];

// 整点时间选项（仅小时，无分钟）
const TIME_OPTIONS = Array.from({ length: 25 }, (_, i) => ({
  label: `${String(i).padStart(2, "0")}:00`,
  value: `${String(i).padStart(2, "0")}:00`,
}));

/** 将 API 时间标准化为 HH:00 格式，确保与 TIME_OPTIONS 匹配 */
function normalizeTime(raw: string | undefined): string {
  if (!raw) return "";
  const m = raw.match(/^(\d{1,2})/);
  if (!m) return "";
  return `${m[1].padStart(2, "0")}:00`;
}

export default function EditTimePage() {
  const navigate = useNavigate();

  const [weekDays, setWeekDays] = useState<string[]>([]);
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  // Picker 弹窗状态
  const [showStartPicker, setShowStartPicker] = useState(false);
  const [showEndPicker, setShowEndPicker] = useState(false);

  // ── 加载达人信息 ──
  const loadStaffInfo = useCallback(async () => {
    try {
      const info = await getStaffDetail();
      console.log("[EditTime] getStaffDetail:", { week_str: info.week_str, start_time: info.start_time, end_time: info.end_time });
      setWeekDays(info.week_str ? String(info.week_str).split(",") : []);
      setStartTime(normalizeTime(String(info.start_time || "")));
      setEndTime(normalizeTime(String(info.end_time || "")));
    } catch (e) {
      console.error("[EditTime] 加载失败:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStaffInfo();
  }, [loadStaffInfo]);

  const toggleDay = (dayKey: number) => {
    const val = String(dayKey);
    setWeekDays((prev) =>
      prev.includes(val) ? prev.filter((x) => x !== val) : [...prev, val],
    );
  };

  // 时间验证：开始 < 结束（数值比较，如 "08:00" < "18:00"）
  const isTimeValid = startTime && endTime && startTime.replace(":", "") < endTime.replace(":", "");

  const handleSubmit = () => {
    if (weekDays.length === 0) return toastWarning("请选择接单日期");
    if (!startTime || !endTime) return toastWarning("请选择接单时间段");
    if (!isTimeValid) return toastWarning("开始时间不能大于结束时间");

    Dialog.show({
      content: "是否确认该接单时间？",
      closeOnAction: true,
      actions: [
        [
          {
            key: "cancel",
            text: "取消",
            style: { color: "#FF6B35", fontWeight: 500 },
            onClick: () => {},
          },
          {
            key: "confirm",
            text: "确认保存",
            bold: true,
            style: { color: "#FF6B35", fontWeight: 600 },
            onClick: async () => {
              setSubmitting(true);
              try {
                const fd = new FormData();
                fd.append("week_str", weekDays.join(","));
                fd.append("start_time", startTime);
                fd.append("end_time", endTime);
                await updateTakeTime(fd);
                toastSuccess("保存成功");
                setTimeout(() => navigate(-1), 1000);
              } catch {
                // 错误已在 request 层统一 toast
              } finally {
                setSubmitting(false);
              }
            },
          },
        ],
      ],
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header — 品牌橙色 */}
      <div style={{ background: BRAND }}>
        <div className="px-5 flex items-center gap-3" style={{ paddingTop: "calc(env(safe-area-inset-top,0px) + 16px)", paddingBottom: 13 }}>
          <button onClick={() => navigate(-1)} className="w-9 h-9 flex items-center justify-center rounded-full cursor-pointer active:scale-[0.98] shrink-0" style={{ background: "rgba(255,255,255,0.2)" }}>
            <i className="ri-arrow-left-line" style={{ fontSize: 20, color: "#fff" }} />
          </button>
          <span className="text-lg font-bold text-white">接单时间</span>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-20">
          <i className="ri-loader-4-line text-2xl animate-spin text-gray-400" />
        </div>
      )}

      {!loading && (<>

      {/* 内容区域 */}
      <div className="px-4 pt-4 space-y-3 pb-28">
        {/* 提示文字 */}
        <div className="flex items-center gap-2 px-1">
          <i className="ri-time-line text-xl" style={{ color: "#FE7A3A" }} />
          <span className="text-base font-bold text-gray-900">请选择接单时间段</span>
        </div>

        {/* 选择日期 */}
        <div className="bg-white rounded-2xl p-4">
          {/* 标题栏：橙色竖条 + 标题 + 提示 */}
          <div className="flex items-center py-2 relative">
            <div className="absolute left-[-16px] top-[7px] w-[5px] h-[22px] rounded-r-full" style={{ background: "#FE7A3A" }} />
            <span className="text-base font-bold text-gray-900 ml-1">选择日期</span>
            <span className="text-xs ml-1.5 text-gray-400">(多选项)</span>
          </div>

          <div className="grid grid-cols-3 gap-2 mt-3">
            {WEEK_DATA.map((day) => {
              const isSelected = weekDays.includes(String(day.key));
              return (
                <button
                  key={day.key}
                  onClick={() => toggleDay(day.key)}
                  className={`py-3 rounded-xl text-sm font-medium active:scale-[0.95] transition-all border-2 ${
                    isSelected ? "text-white" : "bg-[#F8F8F8] border-[#F8F8F8] text-[#333]"
                  }`}
                  style={isSelected ? { background: BRAND, borderColor: BRAND } : {}}
                >
                  {day.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* 工作时间段 */}
        <div className="bg-white rounded-2xl p-4">
          {/* 标题栏：橙色竖条 + 标题 */}
          <div className="flex items-center py-2 relative">
            <div className="absolute left-[-16px] top-[7px] w-[5px] h-[22px] rounded-r-full" style={{ background: "#FE7A3A" }} />
            <span className="text-base font-bold text-gray-900 ml-1">选择工作时间段</span>
          </div>

          <div className="flex items-center justify-center gap-3 mt-4">
            <button
              onClick={() => setShowStartPicker(true)}
              className={`rounded-xl py-3 w-[140px] text-center text-sm transition-colors ${
                startTime && endTime && !isTimeValid ? "bg-red-50 text-red-500" : "bg-[#F8F8F8] text-gray-700"
              } active:bg-gray-100`}
            >
              {startTime || "请选择开始时间"}
            </button>

            <span className="text-gray-400 font-medium">—</span>

            <button
              onClick={() => setShowEndPicker(true)}
              className={`rounded-xl py-3 w-[140px] text-center text-sm transition-colors ${
                startTime && endTime && !isTimeValid ? "bg-red-50 text-red-500" : "bg-[#F8F8F8] text-gray-700"
              } active:bg-gray-100`}
            >
              {endTime || "请选择结束时间"}
            </button>
          </div>

          {/* 时间错误提示 */}
          {startTime && endTime && !isTimeValid && (
            <p className="text-xs text-red-500 mt-2">开始时间不能大于结束时间</p>
          )}
        </div>
      </div>
      </>)}

      {/* 开始时间 Picker */}
      <Picker
        key={`start-${startTime}`}
        columns={[TIME_OPTIONS]}
        value={[startTime]}
        onConfirm={(val) => { setStartTime(val[0]); setShowStartPicker(false); }}
        onClose={() => setShowStartPicker(false)}
        visible={showStartPicker}
      />

      {/* 结束时间 Picker */}
      <Picker
        key={`end-${endTime}`}
        columns={[TIME_OPTIONS]}
        value={[endTime]}
        onConfirm={(val) => { setEndTime(val[0]); setShowEndPicker(false); }}
        onClose={() => setShowEndPicker(false)}
        visible={showEndPicker}
      />

      {/* 底部操作栏 */}
      <div className="fixed bottom-12 left-0 right-0 z-40 px-4 pb-3 pt-2 bg-gradient-to-t from-gray-50 via-gray-50/95 to-transparent">
        <button type="button"
          onClick={handleSubmit}
          disabled={submitting}
          className={`w-full py-3 rounded-2xl text-white text-base font-semibold active:opacity-90 transition-opacity cursor-pointer shadow-lg ${submitting ? "opacity-50" : ""}`}
          style={{ background: `linear-gradient(135deg, ${BRAND}, #ff8c5a)` }}
        >
          {submitting ? "保存中..." : "确定"}
        </button>
      </div>
    </div>
  );
}
